import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Drop, Pill, NotePencil, Heartbeat, ForkKnife, Sparkle, Info } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = [
  { v: 0, l: 'Seg' }, { v: 1, l: 'Ter' }, { v: 2, l: 'Qua' }, { v: 3, l: 'Qui' },
  { v: 4, l: 'Sex' }, { v: 5, l: 'Sáb' }, { v: 6, l: 'Dom' },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-brand-green' : 'bg-brand-bg border border-brand-border'}`} data-testid={testid}>
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

function Section({ icon: Icon, title, hint, enabled, onToggle, testid, children }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center gap-3 mb-1">
        <Icon size={18} className="text-brand-green" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-text">{title}</p>
          {hint && <p className="text-[10px] text-brand-muted mt-0.5">{hint}</p>}
        </div>
        <Toggle checked={enabled} onChange={onToggle} testid={`${testid}-toggle`} />
      </div>
      {enabled && children && <div className="mt-3 pt-3 border-t border-brand-border/40">{children}</div>}
    </div>
  );
}

export default function NotificationPreferencesPanel({ patientId, mode = 'admin' }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const baseUrl = mode === 'admin' && patientId
    ? `${API}/api/patients/${patientId}/notification-preferences`
    : `${API}/api/my/notification-preferences`;

  const fetchPrefs = useCallback(async () => {
    try { const { data } = await axios.get(baseUrl); setPrefs(data); } catch { toast.error('Erro a carregar preferências'); }
  }, [baseUrl]);
  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      const { data } = await axios.put(baseUrl, next);
      setPrefs(data);
    } catch { toast.error('Erro a guardar'); }
    finally { setSaving(false); }
  }, [baseUrl]);

  const patch = (key, value) => {
    const next = { ...(prefs || {}), [key]: { ...((prefs || {})[key] || {}), ...value } };
    setPrefs(next);
    save(next);
  };

  if (!prefs) return <div className="text-xs text-brand-muted">A carregar…</div>;

  const isAdmin = mode === 'admin';

  return (
    <div className="space-y-3" data-testid="notif-prefs-panel">
      <div className="flex items-center gap-2 text-[11px] text-brand-muted bg-brand-mint/10 border border-brand-mint/30 rounded-xl px-3 py-2">
        <Info size={12} className="text-brand-green flex-shrink-0" />
        <span>{isAdmin ? 'Configura as notificações que a paciente vai receber. Ela pode ajustar à medida que precisar.' : 'Estas são as notificações que vais receber. Ajusta a teu gosto.'}</span>
      </div>

      <Section icon={ForkKnife} title="Refeições" hint="Push à hora de cada refeição definida no plano alimentar" enabled={!!prefs.meals?.enabled} onToggle={(v) => patch('meals', { enabled: v })} testid="prefs-meals" />

      <Section icon={Drop} title="Água" hint="Lembrete dentro da janela horária com intervalo fixo" enabled={!!prefs.water?.enabled} onToggle={(v) => patch('water', { enabled: v })} testid="prefs-water">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] text-brand-muted">Intervalo (h)</Label>
            <Input type="number" min="1" max="6" value={prefs.water?.interval_hours || 2} onChange={(e) => patch('water', { interval_hours: parseInt(e.target.value) || 2 })} className="rounded-xl border-brand-border text-xs h-9" />
          </div>
          <div>
            <Label className="text-[10px] text-brand-muted">Início</Label>
            <Input type="time" value={prefs.water?.window_start || '08:00'} onChange={(e) => patch('water', { window_start: e.target.value })} className="rounded-xl border-brand-border text-xs h-9" />
          </div>
          <div>
            <Label className="text-[10px] text-brand-muted">Fim</Label>
            <Input type="time" value={prefs.water?.window_end || '22:00'} onChange={(e) => patch('water', { window_end: e.target.value })} className="rounded-xl border-brand-border text-xs h-9" />
          </div>
        </div>
      </Section>

      <Section icon={Bell} title="Consultas" hint="Lembretes 24h e 1h antes" enabled={!!prefs.appointment?.enabled} onToggle={(v) => patch('appointment', { enabled: v })} testid="prefs-appointment" />

      <Section icon={Heartbeat} title="Check-in semanal" hint="No dia configurado pela nutricionista" enabled={!!prefs.checkin?.enabled} onToggle={(v) => patch('checkin', { enabled: v })} testid="prefs-checkin">
        <div>
          <Label className="text-[10px] text-brand-muted">Hora</Label>
          <Input type="time" value={prefs.checkin?.time || '18:00'} onChange={(e) => patch('checkin', { time: e.target.value })} className="rounded-xl border-brand-border text-xs h-9 w-32" />
        </div>
      </Section>

      <Section icon={NotePencil} title="Diário alimentar" hint="Lembrete diário" enabled={!!prefs.diary?.enabled} onToggle={(v) => patch('diary', { enabled: v })} testid="prefs-diary">
        <div>
          <Label className="text-[10px] text-brand-muted">Hora</Label>
          <Input type="time" value={prefs.diary?.time || '21:00'} onChange={(e) => patch('diary', { time: e.target.value })} className="rounded-xl border-brand-border text-xs h-9 w-32" />
        </div>
      </Section>

      <Section icon={Pill} title="Suplementação" hint="Push à hora definida em cada suplemento" enabled={!!prefs.supplements?.enabled} onToggle={(v) => patch('supplements', { enabled: v })} testid="prefs-supplements" />

      <Section icon={Sparkle} title="Específicos do programa" hint="Hidratação extra (Super Mamãs), introdução alimentar (Super Bebés)" enabled={!!prefs.program_specific?.enabled} onToggle={(v) => patch('program_specific', { enabled: v })} testid="prefs-program-specific" />

      {isAdmin && (
        <Section icon={Sparkle} title="Mensagem motivacional semanal" hint="Escolhe o dia, hora e mensagem personalizada" enabled={!!prefs.motivational?.enabled} onToggle={(v) => patch('motivational', { enabled: v })} testid="prefs-motivational">
          <div className="space-y-2">
            <Textarea value={prefs.motivational?.message || ''} onChange={(e) => patch('motivational', { message: e.target.value })} rows={2} placeholder="Ex: Estás a fazer um trabalho incrível. Continua!" className="rounded-xl border-brand-border text-xs resize-none" data-testid="motivational-message" />
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-brand-muted">Dia:</Label>
              <div className="flex gap-1 flex-wrap">
                {WEEKDAYS.map(d => (
                  <button key={d.v} type="button" onClick={() => patch('motivational', { weekday: d.v })} className={`text-[10px] px-2 py-1 rounded-full ${prefs.motivational?.weekday === d.v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{d.l}</button>
                ))}
              </div>
              <Input type="time" value={prefs.motivational?.time || '09:00'} onChange={(e) => patch('motivational', { time: e.target.value })} className="rounded-xl border-brand-border text-xs h-8 w-24 ml-auto" />
            </div>
          </div>
        </Section>
      )}

      {saving && <p className="text-[10px] text-brand-muted text-center">A guardar…</p>}
    </div>
  );
}
